import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label } from '@lensora/ui'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'


import { authClient } from '@/lib/auth'

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [password, setPassword] = React.useState('')
  const [confirm, setConfirm] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [done, setDone] = React.useState(false)
  // Supabase fires PASSWORD_RECOVERY via onAuthStateChange after exchanging
  // the code/token from the invite link. Works for both PKCE and implicit flows.
  const [isRecovery, setIsRecovery] = React.useState(false)

  React.useEffect(() => {
    return authClient.onAuthStateChange((_session, event) => {
      if (event === 'PASSWORD_RECOVERY') setIsRecovery(true)
    })
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    setIsLoading(true)
    try {
      await authClient.updatePassword(password)
      setDone(true)
      setTimeout(() => navigate('/dashboard', { replace: true }), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-raised p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 rounded-lg bg-brand-primary" />
          <h1 className="text-2xl font-bold text-text">Set new password</h1>
          <p className="mt-1 text-sm text-text-muted">Choose a strong password for your account</p>
        </div>

        <Card>
          {done ? (
            <>
              <CardHeader>
                <CardTitle className="text-base">Password updated</CardTitle>
                <CardDescription>Redirecting you to the dashboard…</CardDescription>
              </CardHeader>
            </>
          ) : !isRecovery ? (
            <>
              <CardHeader>
                <CardTitle className="text-base">Link expired or invalid</CardTitle>
                <CardDescription>
                  This reset link is no longer valid. Request a new one from the sign-in page.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="w-full" onClick={() => navigate('/login', { replace: true })}>
                  Back to sign in
                </Button>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle className="text-base">Choose a new password</CardTitle>
                <CardDescription>Must be at least 8 characters.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                    />
                  </div>
                  {error && <p className="text-sm text-feedback-error">{error}</p>}
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? 'Updating…' : 'Update password'}
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  )
}
